import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Form, Skeleton, Tabs } from "antd";
import {
  FormCustom,
  ButtonsCustom,
  InputCustom,
  SwitchCustomPro,
  InputNumberCustom,
  CheckBoxCustom
} from "src/components/custom";

import {
  LoadingOutlined
} from '@ant-design/icons';

import { RestApiClientContext } from "src/hooks/context/pageContext";

import "./addEstablishment.sass";
import FormItem from "antd/es/form/FormItem";
import { Helmet } from "react-helmet-async";

interface AddEstablishmentProps {
  id: string;
  setVisible: (state: boolean) => void;
  reload: () => void;
  data?: any;
  roomList: any;
  regimenList: any;
}

interface DataEstablishment {
  identityCode: string;
  category: number;
  idCategoryType: number;
  name: string;
  roomsNumber: number;
  isActive: boolean;
  id?: string;

  regimens: any[];
  roomTypes: any[];
}

const { TabPane } = Tabs;

export const AddEstablishment = (props: AddEstablishmentProps) => {
  const { t } = useTranslation(["config"]);
  const { setVisible, reload } = props;
  const restApiClient = useContext(RestApiClientContext);
  const [form] = Form.useForm();
  const [savingData, setSavingData] = useState<boolean>(false);


  const [fieldsData, setFieldsData] = useState<DataEstablishment>();


  const [rooms, setRooms] = useState<any[]>([]);
  useEffect(() => {
    setRooms(props.roomList.roomTypes);
  }, [props.roomList])

  const [regimens, setRegimens] = useState<any[]>([]);
  useEffect(() => {
    setRegimens(props.regimenList.regimens);
  }, [props.regimenList])

  useEffect(() => {
    if (props.data) {
      setFieldsData(props.data);
    } else {
      form.resetFields();
    }
  }, [props.data]);

  const handleOnChange = (e) => {
    setFieldsData({ ...fieldsData, [e[0].name]: e[0].value });
  };

  const handleRegimens = (e) => {
    const newRegimens = regimens.map((r) => {
      if (r.id === e.target.id) {
        return { ...r, selected: !r.selected };
      } else {
        return { ...r, selected: r.selected ?? false };
      }
    });
    setRegimens(newRegimens);
    setFieldsData({ ...fieldsData, regimens: newRegimens });
  };

  const handleRooms = (e) => {
    const newRooms = rooms.map((r) => {
      if (r.id === e.target.id) {
        return { ...r, selected: !r.selected };
      } else {
        return { ...r, selected: r.selected ?? false };
      }
    });
    setRooms(newRooms);
    setFieldsData({ ...fieldsData, roomTypes: newRooms });
  };

  const handleSave = async () => {
    const body: DataEstablishment = {
      name: form.getFieldValue("name"),
      identityCode: form.getFieldValue("identityCode"),
      category: +form.getFieldValue("category"),
      idCategoryType: +form.getFieldValue("idCategoryType") ?? 1,
      roomsNumber: +form.getFieldValue("roomsNumber"),
      isActive: form.getFieldValue("isActive"),
      regimens: fieldsData?.regimens ? fieldsData?.regimens.filter(regimen => regimen.selected ? true : false).map(regimen => regimen.id) : [],
      roomTypes: fieldsData?.roomTypes ? fieldsData?.roomTypes.filter(room => room.selected ? true : false).map(room => room.id) : []
    };

    setSavingData(true);
    restApiClient.createEstablishment(body)
      .then((r: any) => {
        reload();
        form.resetFields();
        setRooms(props.roomList.roomTypes);
        setRegimens(props.regimenList.regimens);
        setVisible(false)
      }).finally(() => {
        setSavingData(false);
      });
  };

  const listOptions = [1, 2, 3, 4, 5];
  const listImages = [
    { value: 1, icon: "\uf005" },
    { value: 2, icon: "\uf084" }
  ];

  return (
    <div>
      <Helmet>
        <link href="https://maxcdn.bootstrapcdn.com/font-awesome/4.6.1/css/font-awesome.min.css" rel="stylesheet" />
      </Helmet>
      <FormCustom
        onFinish={handleSave}
        form={form}
        layout="vertical"
        id="edit-establishment"
        onFieldsChange={handleOnChange}
      >
        <Form.Item
          className="col form-item"
          label={t("establishments.drawer-name")}
          name="name"
          rules={[
            {
              whitespace: true,
              message: t("validations.name"),
              required: true
            }
          ]}
        >
          <InputCustom placeholder={t("establishments.drawer-placeholder")} />
        </Form.Item>
        <Form.Item
          className="col form-item"
          label={t("establishments.drawer-code")}
          name="identityCode"
          rules={[
            {
              whitespace: true,
              message: t("validations.code")
            }
          ]}
        >
          <InputCustom placeholder={t("establishments.drawer-placeholder")} />
        </Form.Item>
        <Form.Item className="col-md-6 form-item" label={t("establishments.drawer-rooms")} name="roomsNumber" rules={[
          {
            message: t("validations.rooms"),
            required: true
          }
        ]}>
          <InputNumberCustom placeholder={t("establishments.drawer-placeholder")} />
        </Form.Item>
        <Form.Item name="isActive" valuePropName="checked" label={t("channels.drawer-isActive")}>
          <SwitchCustomPro
            checked
            checkedChildren={t("channels.drawer-yes")}
            unCheckedChildren={t("channels.drawer-no")}
            onChange={() => setFieldsData({ ...fieldsData, isActive: !fieldsData?.isActive })}
          />
        </Form.Item>
        <span>{t("establishments.drawer-category")}</span>
        <div className="category-group d-flex">
          <FormItem
            className="col"
            name="category"
            initialValue={1}
            rules={[
              {
                required: true,
                message: t("validations.category")
              }
            ]}
          >
            <div className="select_box">
              <select className="category" defaultValue={fieldsData?.category}>
                {listOptions.map((opt, i) => (
                  <option value={opt} key={i}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          </FormItem>
          <Form.Item
            className="col"
            name="idCategoryType"
            initialValue={1}
            rules={[
              {
                required: true,
                message: t("validations.category")
              }
            ]}
          >
            <div className="select_box">
              <select className="category-type category" defaultValue={fieldsData?.idCategoryType}>
                {listImages.map((opt, i) => (
                  <option value={opt.value} key={i}>
                    {opt.icon}
                  </option>
                ))}
              </select>
            </div>
          </Form.Item>
        </div>
        <Tabs
          className="form-item"
          defaultActiveKey="0"
        >
          <TabPane key={0} tab={<div className="tab-header">{t("establishments.tabs.roomTypes")}</div>}>
            {rooms?.map((r, i) => (
              <label className="row" key={i}>
                <CheckBoxCustom
                  id={r.id}
                  checked={r.selected ? r.selected : false}
                  content={r.name}
                  onChange={handleRooms}
                />
              </label>
            ))}
          </TabPane>
          <TabPane key={1} tab={<div className="tab-header">{t("establishments.tabs.regimens")}</div>}>
            {regimens?.map((r, i) => {
              return (
                <label className="row" key={i}>
                  <CheckBoxCustom
                    id={r.id}
                    checked={r.selected ? r.selected : false}
                    content={r.name}
                    onChange={handleRegimens}
                  />
                </label>
              );
            })}
          </TabPane>
        </Tabs>
        <Form.Item className="form-item">
          <div className="d-flex buttons justify-content-end">
            <ButtonsCustom
              className="btn-secondary"
              htmlType="button"
              onClick={() => setVisible(false)}
              content={<span>{t("establishments.drawer-cancel")}</span>}
            />
            <ButtonsCustom
              disabled={savingData}
              className="btn-primary"
              type="primary"
              htmlType="submit"
              content={<span>{savingData ? <LoadingOutlined /> : ''} {t("establishments.drawer-save")}</span>}
            />
          </div>
        </Form.Item>
      </FormCustom>
    </div>
  );
};
